<script setup lang="ts">
import datePicker from '@/components/date-picker.vue';
import FlightStatusError from '@/components/FlightStatusError.vue';
import LocationAutoComplete from '@/components/locationAutoComplete.vue';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { useLocations } from '@/composables/useLocations';
import MainLayout from '@/layouts/app/MainLayout.vue';
import FlightListing from '@/pages/flightStatus/listing.vue';
import { useForm } from '@inertiajs/vue3';
import axios from 'axios';
import moment from 'moment';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { formatFlightDate } = useDateFormatter();

// Add this line to define props
const props = defineProps<{
    flights?: any[];
    error?: string | null;
    error_code?: string | null;
    error_details?: any;
}>();

const isSearchEnabled = ref(true);
const isLocationDialogOpen = ref(false);
const searchType = ref<string | null>(null);
const searchedType = ref<string | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const errorCode = ref<string | null>(null);
const errorDetails = ref<any>(null);
const success = ref<string | null>(null);
const flights = ref<any[]>([]);
const returnFlights = ref<any[]>([]);
const returnDate = ref<string | null>(null);

// Computed: is this a round-trip search?
const isRoundTrip = computed(() => !!returnDate.value);

// Create separate reactive variables for each location
const departureLocation = ref<any>(null);
const arrivalLocation = ref<any>(null);
const departureLocationByFlightNumber = ref<any>(null);
const arrivalLocationByFlightNumber = ref<any>(null);
const selectedDate = ref(new Date().toISOString().split('T')[0]); // Today's date in YYYY-MM-DD format
const airline = ref('');
const flightNumber = ref('');
const defaultSortby = ref('dep_asc');

// Add individual error states for each field
const departureError = ref('');
const arrivalError = ref('');
const dateError = ref('');
const flightNumberError = ref('');

// Add error type tracking for language switching
const errorTypes = ref({
    departure: null as string | null,
    arrival: null as string | null,
    date: null as string | null,
    flightNumber: null as string | null,
});

// Computed properties for displaying location values in text fields
const departureDisplayValue = computed(() => {
    if (!departureLocation.value) return '';
    return departureLocation.value.city ? departureLocation.value.city : '';
});

const arrivalDisplayValue = computed(() => {
    if (!arrivalLocation.value) return '';
    return arrivalLocation.value.city ? arrivalLocation.value.city : '';
});

// Computed display value for selected date (mobile activator), localized
const selectedDateDisplay = computed(() => {
    return formatFlightDate(selectedDate.value, 'DD MMMM');
});

// Computed display value for return date (mobile activator), localized
const returnDateDisplay = computed(() => {
    if (!returnDate.value) return '';
    return formatFlightDate(returnDate.value, 'DD MMMM');
});

const formatDate = (dateString: string | null) => {
    if (!dateString) return '';

    // Parse the YYYY-MM-DD string to create a proper date
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues

    // Get day name from translations
    const dayNames = [
        t('date.days.sunday'),
        t('date.days.monday'),
        t('date.days.tuesday'),
        t('date.days.wednesday'),
        t('date.days.thursday'),
        t('date.days.friday'),
        t('date.days.saturday'),
    ];

    // Get month name from translations
    const monthNames = [
        t('date.months.january'),
        t('date.months.february'),
        t('date.months.march'),
        t('date.months.april'),
        t('date.months.may'),
        t('date.months.june'),
        t('date.months.july'),
        t('date.months.august'),
        t('date.months.september'),
        t('date.months.october'),
        t('date.months.november'),
        t('date.months.december'),
    ];

    const dayName = dayNames[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    // return `${dayName}, ${day} ${month} `;
    return `${day} ${month} `;
};

// Optional: Handle location selection events
const onDepartureSelected = (location: any) => {
    if (arrivalLocation.value && location && arrivalLocation.value.code === location.code) {
        arrivalLocation.value = null;
    }
    departureLocation.value = location;
};

const onArrivalSelected = (location: any) => {
    if (departureLocation.value && location && departureLocation.value.code === location.code) {
        departureLocation.value = null;
    }
    arrivalLocation.value = location;
};

const { t, locale } = useI18n();
const { getLocationByCode } = useLocations();

watch(locale, () => {
    // Update departure location with translated data
    if (departureLocation.value && departureLocation.value.code) {
        const translatedDeparture = getLocationByCode(departureLocation.value.code);
        if (translatedDeparture) {
            departureLocation.value = translatedDeparture;
        }
    }

    // Update arrival location with translated data
    if (arrivalLocation.value && arrivalLocation.value.code) {
        const translatedArrival = getLocationByCode(arrivalLocation.value.code);
        if (translatedArrival) {
            arrivalLocation.value = translatedArrival;
        }
    }

    // Update error messages when language changes
    updateErrorMessages();
});

// Function to update error messages based on current validation state
const updateErrorMessages = () => {
    if (errorTypes.value.departure) {
        departureError.value = t('validation.departure_required');
    }
    if (errorTypes.value.arrival) {
        arrivalError.value = t('validation.arrival_required');
    }
    if (errorTypes.value.date) {
        dateError.value = t('validation.date_required');
    }
    if (errorTypes.value.flightNumber === 'required') {
        flightNumberError.value = t('validation.flight_number_required');
    } else if (errorTypes.value.flightNumber === 'format') {
        flightNumberError.value = t('validation.flight_number_format');
    }
};

const form = useForm({
    departureairport: '',
    arrivalairport: '',
    departuredate: '',
    flightnumber: '',
    airline: '',
    // searchType: '',
    language: '',
});
const submitDetail = async (isFromReturnDateClear: boolean | Event = false) => {
    departureError.value = '';
    arrivalError.value = '';
    dateError.value = '';
    error.value = null;
    errorCode.value = null;
    errorDetails.value = null;

    let flight_number = '';
    let airline = '';
    form.flightnumber = '';
    form.airline = '';
    form.language = '';

    let hasErrors = false;
    if (searchType.value === 'by_code') {
        if (!departureLocation.value && !arrivalLocation.value) {
            if (isFromReturnDateClear === true) {
                flights.value = [];
                returnFlights.value = [];
                return;
            }
            errorTypes.value.departure = 'required';
            departureError.value = t('validation.departure_required');
            errorTypes.value.arrival = 'required';
            arrivalError.value = t('validation.arrival_required');
            hasErrors = true;
        }
        if (flightNumber.value !== '') {
            ignoreNextWatcher = true;
            flightNumber.value = '';
        }
        flight_number = '';
        airline = '';
        errorTypes.value.flightNumber = null;
        flightNumberError.value = '';
    } else {
        if (!flightNumber.value) {
            errorTypes.value.flightNumber = 'required';
            flightNumberError.value = t('validation.flight_number_required');
            hasErrors = true;
        }

        if (departureLocation.value !== null || arrivalLocation.value !== null) {
            ignoreNextWatcher = true;
            departureLocation.value = null;
            arrivalLocation.value = null;
        }
        flight_number = flightNumber.value.replace(/[^0-9]/g, '');
        airline = flightNumber.value.replace(/[^A-Za-z]/g, '');
        errorTypes.value.departure = null;
        errorTypes.value.arrival = null;
        departureError.value = '';
        arrivalError.value = '';
    }

    if (!selectedDate.value) {
        errorTypes.value.date = 'required';
        dateError.value = t('validation.date_required');
        hasErrors = true;
    } else {
        errorTypes.value.date = null;
        dateError.value = '';
    }
    if (hasErrors) {
        flights.value = [];
        returnFlights.value = [];
        return;
    }

    try {
        const departureCode = departureLocation.value?.code;
        const arrivalCode = arrivalLocation.value?.code;

        if (departureCode) {
            defaultSortby.value = 'dep_asc';
        } else if (arrivalCode) {
            defaultSortby.value = 'arr_asc';
        }

        const params: Record<string, string> = {
            airline: airline || '',
            flightnumber: flight_number || '',
            departureairport: departureCode || '',
            arrivalairport: arrivalCode || '',
            departuredate: moment(selectedDate.value).format('YYYY-MM-DD'),
            language: locale.value,
        };

        // Include arrivaldate in URL when round-trip
        const urlParams: Record<string, string> = { ...params };
        if (returnDate.value) {
            urlParams.arrivaldate = moment(returnDate.value).format('YYYY-MM-DD');
        }

        const baseUrl = window.location.origin + window.location.pathname;
        const queryString = new URLSearchParams(urlParams).toString();
        window.history.pushState({}, '', `${baseUrl}?${queryString}`);

        // isSearchEnabled.value = false; // COMMENTED OUT: always show form
        flights.value = [];
        returnFlights.value = [];
        loading.value = true;
        departureLocationByFlightNumber.value = null;
        arrivalLocationByFlightNumber.value = null;

        // Build outbound API call
        const outboundPromise = axios.get('/api/flight-legs', { params });

        // Build return API call (swap airports, use return date) — only if round-trip
        let returnPromise: Promise<any> | null = null;
        if (isRoundTrip.value && departureCode && arrivalCode) {
            const returnParams = {
                ...params,
                departureairport: arrivalCode, // swap
                arrivalairport: departureCode, // swap
                departuredate: moment(returnDate.value).format('YYYY-MM-DD'),
            };
            returnPromise = axios.get('/api/flight-legs', { params: returnParams });
        }

        // Execute in parallel
        const [outboundResponse, returnResponse] = await Promise.all([outboundPromise, returnPromise || Promise.resolve(null)]);

        // Process outbound
        if (outboundResponse.data?.data?.length) {
            flights.value = outboundResponse.data.data;
            searchedType.value = searchType.value;
            success.value = t('flight_status.form_submitted_successfully');

            if (searchType.value === 'by_flight_number') {
                const flightDepartureCode = flights.value[0].departure.airport_code;
                const flightArrivalCode = flights.value[0].arrival.airport_code;
                departureLocationByFlightNumber.value = getLocationByCode(flightDepartureCode);
                arrivalLocationByFlightNumber.value = getLocationByCode(flightArrivalCode);
            }
        } else if (outboundResponse.data?.error) {
            error.value = outboundResponse.data.error;
            errorCode.value = outboundResponse.data.error_code;
            errorDetails.value = outboundResponse.data.error_details;
        } else {
            error.value = t('flight_status.network_error');
        }

        // Process return flights
        if (returnResponse?.data?.data?.length) {
            returnFlights.value = returnResponse.data.data;
        }
    } catch (err) {
        loading.value = false;
        if (err.response?.data?.message) {
            error.value = err.response.data.message;
            errorCode.value = err.response.data.error_code || null;
            errorDetails.value = err.response.data.error_details || null;
        } else if (err.response?.data?.errors) {
            const errorMessages = Object.values(err.response.data.errors).flat();
            error.value = errorMessages.join(', ');
        } else {
            error.value = t('flight_status.network_error');
        }
    } finally {
        loading.value = false;
        // isSearchEnabled.value = false; // COMMENTED OUT: always show form
    }
};

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let ignoreNextWatcher = false;
watch(
    [departureLocation, arrivalLocation, selectedDate, returnDate, flightNumber, searchType],
    () => {
        if (ignoreNextWatcher) {
            ignoreNextWatcher = false;
            return;
        }
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (searchType.value === 'by_code') {
                if ((departureLocation.value || arrivalLocation.value) && selectedDate.value) {
                    submitDetail();
                } else if (!departureLocation.value && !arrivalLocation.value) {
                    flights.value = [];
                    returnFlights.value = [];
                }
            } else if (searchType.value === 'by_flight_number') {
                if (flightNumber.value && flightNumber.value.length >= 3 && selectedDate.value) {
                    submitDetail();
                } else if (!flightNumber.value) {
                    flights.value = [];
                    returnFlights.value = [];
                }
            }
        }, 600); // 600ms debounce
    },
    { deep: true },
);

const locationDialogOpen = (isOpen: boolean) => {
    if (!isOpen) {
        isLocationDialogOpen.value = false;
    }
};

const locationDialogAfterOpen = (isOpen: boolean) => {
    isLocationDialogOpen.value = true;
};

const getLocationDetails = (airportCode: string) => {
    const location = getLocationByCode(airportCode);
    return location || { city: '', address: airportCode, code: airportCode };
};

onMounted(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.toString() === '') return; // no params

    const params: any = Object.fromEntries(urlParams.entries());

    // --- Set form fields & v-models ---
    if (params.departuredate) {
        selectedDate.value = params.departuredate;
    }

    // Parse return date for round-trip
    if (params.arrivaldate) {
        returnDate.value = params.arrivaldate;
    }

    if (params.flightnumber) {
        flightNumber.value = (params.airline || '') + params.flightnumber;
    }

    if (params.language && params.language !== locale.value) {
        locale.value = params.language;
    }

    // Set locations (if available)
    if (params.departureairport) {
        const dep = getLocationByCode(params.departureairport);
        if (dep) departureLocation.value = dep;
    }

    if (params.arrivalairport) {
        const arr = getLocationByCode(params.arrivalairport);
        if (arr) arrivalLocation.value = arr;
    }

    // Set search type automatically
    if (params.flightnumber) {
        searchType.value = 'by_flight_number';
    } else {
        searchType.value = 'by_code';
    }

    // --- Fetch results ---
    ignoreNextWatcher = true;
    submitDetail();
});

watch(
    () => form.processing,
    (isProcessing) => {
        loading.value = isProcessing;
    },
);
</script>

<template>
    <MainLayout>
        <v-container>
            <!-- v-if="isSearchEnabled" COMMENTED OUT: always show form -->
            <div class="fs-booking-form fs-booking-form-float">
                <v-card>
                    <v-tabs v-model="searchType">
                        <v-tab value="by_code">{{ t('flight_status.by_route') }}</v-tab>
                        <v-tab value="by_flight_number">{{ t('flight_status.by_flight_number') }}</v-tab>
                    </v-tabs>
                    <v-card-text>
                        <v-tabs-window v-model="searchType">
                            <v-tabs-window-item value="by_code">
                                <v-form class="fs-form" @submit.prevent="submitDetail">
                                    <v-dialog
                                        transition="dialog-bottom-transition"
                                        fullscreen
                                        @update:modelValue="locationDialogOpen"
                                        @afterEnter="locationDialogAfterOpen"
                                    >
                                        <template v-slot:activator="{ props: activatorProps }">
                                            <div class="fs-form-field fs-desktop-hidden">
                                                <v-text-field
                                                    v-bind="activatorProps"
                                                    :model-value="departureDisplayValue"
                                                    :label="t('flight_status.departure')"
                                                    readonly
                                                >
                                                    <template v-slot:append-inner v-if="departureLocation">
                                                        <v-icon
                                                            icon="mdi-close"
                                                            @click.stop.prevent="
                                                                departureLocation = null;
                                                                submitDetail();
                                                            "
                                                        />
                                                    </template>
                                                </v-text-field>
                                                <div v-if="departureError" class="error-message" style="color: red; font-size: 12px; margin-top: 4px">
                                                    {{ departureError }}
                                                </div>
                                            </div>
                                        </template>
                                        <template v-slot:default="{ isActive }">
                                            <v-card>
                                                <v-toolbar>
                                                    <v-btn icon="mdi-close" @click="isActive.value = false"></v-btn>
                                                </v-toolbar>
                                                <v-list>
                                                    <div class="fs-form-field fs-autocomplete">
                                                        <LocationAutoComplete
                                                            :isLocationDialogOpen="isLocationDialogOpen"
                                                            v-model="departureLocation"
                                                            :label="t('flight_status.departure')"
                                                            :is-in-dialog="true"
                                                            @location-selected="
                                                                (loc) => {
                                                                    onDepartureSelected(loc);
                                                                    isActive.value = false;
                                                                }
                                                            "
                                                        />
                                                    </div>
                                                </v-list>
                                            </v-card>
                                        </template>
                                    </v-dialog>
                                    <v-dialog
                                        transition="dialog-bottom-transition"
                                        fullscreen
                                        @update:modelValue="locationDialogOpen"
                                        @afterEnter="locationDialogAfterOpen"
                                    >
                                        <template v-slot:activator="{ props: activatorProps }">
                                            <div class="fs-form-fields fs-desktop-hidden">
                                                <div class="fs-form-field">
                                                    <v-text-field
                                                        v-bind="activatorProps"
                                                        :model-value="arrivalDisplayValue"
                                                        :label="t('flight_status.arrival')"
                                                        readonly
                                                    >
                                                        <template v-slot:append-inner v-if="arrivalLocation">
                                                            <v-icon
                                                                icon="mdi-close"
                                                                @click.stop.prevent="
                                                                    arrivalLocation = null;
                                                                    submitDetail();
                                                                "
                                                            />
                                                        </template>
                                                    </v-text-field>
                                                    <div
                                                        v-if="arrivalError"
                                                        class="error-message"
                                                        style="color: red; font-size: 12px; margin-top: 4px"
                                                    >
                                                        {{ arrivalError }}
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-slot:default="{ isActive }">
                                            <v-card>
                                                <v-toolbar>
                                                    <v-btn icon="mdi-close" @click="isActive.value = false"></v-btn>
                                                </v-toolbar>
                                                <v-list>
                                                    <div class="fs-form-field fs-autocomplete">
                                                        <LocationAutoComplete
                                                            :isLocationDialogOpen="isLocationDialogOpen"
                                                            v-model="arrivalLocation"
                                                            :label="t('flight_status.arrival')"
                                                            :is-in-dialog="true"
                                                            @location-selected="
                                                                (loc) => {
                                                                    onArrivalSelected(loc);
                                                                    isActive.value = false;
                                                                }
                                                            "
                                                        />
                                                    </div>
                                                </v-list>
                                            </v-card>
                                        </template>
                                    </v-dialog>
                                    <v-dialog transition="dialog-bottom-transition" fullscreen>
                                        <template v-slot:activator="{ props: activatorProps }">
                                            <div class="fs-form-fields fs-desktop-hidden">
                                                <div class="fs-form-field">
                                                    <v-text-field
                                                        v-bind="activatorProps"
                                                        :model-value="selectedDateDisplay"
                                                        :label="t('flight_status.date')"
                                                        readonly
                                                    />
                                                    <div v-if="dateError" class="error-message" style="color: red; font-size: 12px; margin-top: 4px">
                                                        {{ dateError }}
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-slot:default="{ isActive }">
                                            <v-card>
                                                <v-toolbar>
                                                    <v-toolbar-title>{{ t('flight_status.flight_date') }}</v-toolbar-title>
                                                    <v-btn icon="mdi-close" @click="isActive.value = false"></v-btn>
                                                </v-toolbar>
                                                <v-list>
                                                    <div class="fs-form-fields">
                                                        <!-- <v-date-picker v-model="selectedDate" color="primary"
                                                            ></v-date-picker> -->
                                                        <datePicker v-model="selectedDate" :isMenu="false" />
                                                    </div>
                                                </v-list>
                                            </v-card>
                                        </template>
                                    </v-dialog>
                                    <!-- Mobile return date picker dialog -->
                                    <v-dialog transition="dialog-bottom-transition" fullscreen>
                                        <template v-slot:activator="{ props: activatorProps }">
                                            <div class="fs-form-fields fs-desktop-hidden">
                                                <div class="fs-form-field">
                                                    <v-text-field
                                                        v-bind="activatorProps"
                                                        :model-value="returnDateDisplay"
                                                        :label="t('flight_status.return_date')"
                                                        readonly
                                                    >
                                                        <template v-slot:append-inner v-if="returnDate">
                                                            <v-icon
                                                                icon="mdi-close"
                                                                @click.stop.prevent="
                                                                    returnDate = null;
                                                                    submitDetail(true);
                                                                "
                                                            />
                                                        </template>
                                                    </v-text-field>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-slot:default="{ isActive }">
                                            <v-card>
                                                <v-toolbar>
                                                    <v-toolbar-title>{{ t('flight_status.return_date') }}</v-toolbar-title>
                                                    <v-btn icon="mdi-close" @click="isActive.value = false"></v-btn>
                                                </v-toolbar>
                                                <v-list>
                                                    <div class="fs-form-fields">
                                                        <datePicker v-model="returnDate" :isMenu="false" />
                                                    </div>
                                                </v-list>
                                            </v-card>
                                        </template>
                                    </v-dialog>
                                    <div class="fs-form-fields-autocomplete fs-form-fields fs-mobile-hidden">
                                        <div class="fs-form-field fs-autocomplete">
                                            <LocationAutoComplete
                                                v-model="departureLocation"
                                                :label="t('flight_status.departure')"
                                                @location-selected="onDepartureSelected"
                                            />
                                            <div v-if="departureError" class="error-message" style="color: red; font-size: 12px; margin-top: 4px">
                                                {{ departureError }}
                                            </div>
                                        </div>
                                        <div class="fs-form-field fs-autocomplete">
                                            <LocationAutoComplete
                                                v-model="arrivalLocation"
                                                :label="t('flight_status.arrival')"
                                                @location-selected="onArrivalSelected"
                                            />
                                            <div v-if="arrivalError" class="error-message" style="color: red; font-size: 12px; margin-top: 4px">
                                                {{ arrivalError }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="fs-mobile-hidden">
                                        <datePicker v-model="selectedDate" />
                                        <div v-if="dateError" class="error-message" style="color: red; font-size: 12px; margin-top: 4px">
                                            {{ dateError }}
                                        </div>
                                    </div>
                                    <!-- Return date picker (desktop) -->
                                    <div class="fs-mobile-hidden">
                                        <div class="fs-form-field fs-date-input">
                                            <div class="d-flex justify-center">
                                                <v-menu :close-on-content-click="false" location="bottom center">
                                                    <template v-slot:activator="{ props: menuProps }">
                                                        <v-text-field
                                                            v-bind="menuProps"
                                                            :model-value="returnDateDisplay"
                                                            :label="t('flight_status.return_date')"
                                                            readonly
                                                        >
                                                            <template v-slot:append-inner v-if="returnDate">
                                                                <v-icon
                                                                    icon="mdi-close"
                                                                    @click.stop.prevent="
                                                                        returnDate = null;
                                                                        submitDetail(true);
                                                                    "
                                                                />
                                                            </template>
                                                        </v-text-field>
                                                    </template>
                                                    <v-locale-provider :locale="locale">
                                                        <v-date-picker
                                                            :title="t('vuetify.datePicker.title')"
                                                            :model-value="returnDate ? new Date(returnDate + 'T00:00:00') : null"
                                                            color="primary"
                                                            @update:model-value="
                                                                (val: Date | string | null) => {
                                                                    if (val instanceof Date) {
                                                                        const y = val.getFullYear();
                                                                        const m = String(val.getMonth() + 1).padStart(2, '0');
                                                                        const d = String(val.getDate()).padStart(2, '0');
                                                                        returnDate = `${y}-${m}-${d}`;
                                                                    } else {
                                                                        returnDate = val;
                                                                    }
                                                                }
                                                            "
                                                        />
                                                    </v-locale-provider>
                                                </v-menu>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- <div class="fs-btn">
                                        <v-btn type="submit" block color="primary" :loading="loading"
                                            :disabled="loading">
                                            {{ loading ? t('common.loading') : t('flight_status.search_flights')
                                            }}
                                        </v-btn>
                                    </div> -->
                                </v-form>
                            </v-tabs-window-item>

                            <v-tabs-window-item value="by_flight_number">
                                <v-form class="fs-form fs-form-flight-number" @submit.prevent="submitDetail">
                                    <div class="fs-form-field">
                                        <v-text-field v-model="flightNumber" :label="t('flight_status.flight_number')" />
                                        <div v-if="flightNumberError" class="error-message" style="color: red; font-size: 12px; margin-top: 4px">
                                            {{ flightNumberError }}
                                        </div>
                                    </div>
                                    <v-dialog transition="dialog-bottom-transition" fullscreen>
                                        <template v-slot:activator="{ props: activatorProps }">
                                            <div class="fs-form-fields fs-desktop-hidden">
                                                <div class="fs-form-field">
                                                    <v-text-field
                                                        v-bind="activatorProps"
                                                        :model-value="selectedDateDisplay"
                                                        :label="t('flight_status.date')"
                                                        readonly
                                                    />
                                                    <div v-if="dateError" class="error-message" style="color: red; font-size: 12px; margin-top: 4px">
                                                        {{ dateError }}
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-slot:default="{ isActive }">
                                            <v-card>
                                                <v-toolbar>
                                                    <v-toolbar-title>{{ t('flight_status.flight_date') }}</v-toolbar-title>
                                                    <v-btn icon="mdi-close" @click="isActive.value = false"></v-btn>
                                                </v-toolbar>
                                                <v-list>
                                                    <div class="fs-form-fields">
                                                        <!-- <v-date-picker v-model="selectedDate" color="primary"
                                                            @update:model-value="
                                                                () => {
                                                                    isActive.value = false;
                                                                }
                                                            "></v-date-picker> -->
                                                        <datePicker v-model="selectedDate" :isMenu="false" />
                                                    </div>
                                                </v-list>
                                            </v-card>
                                        </template>
                                    </v-dialog>
                                    <div class="fs-mobile-hidden">
                                        <datePicker v-model="selectedDate" />
                                        <div v-if="dateError" class="error-message" style="color: red; font-size: 12px; margin-top: 4px">
                                            {{ dateError }}
                                        </div>
                                    </div>
                                </v-form>
                            </v-tabs-window-item>
                        </v-tabs-window>
                    </v-card-text>
                </v-card>
            </div>
            <!-- COMMENTED OUT: Collapsed bar (kept for potential future use)
            <div class="fs-booking-form fs-booking-edit-form fs-booking-edit-form-float" v-else>
                <v-card>
                    <v-card-text>
                        <v-form class="fs-form">
                            <div class="fs-booking-edit-form-datepicker">
                                <datePicker v-model="selectedDate" />
                            </div>
                            <div class="fs-date-in">
                                <h4>{{ t('flight_status.date') }}</h4>
                                <strong>{{ formatFlightDate(selectedDate, 'DD MMMM') }}</strong>
                            </div>
                            <div class="fs-booking-edit-autocomplete-text" v-if="!loading">
                                <template v-if="searchType == 'by_flight_number'">
                                    <div class="fs-booking-edit-autocomplete-text-item"
                                        v-if="departureLocationByFlightNumber">
                                        <h3 v-if="departureLocationByFlightNumber.city">{{ departureLocationByFlightNumber.city }}<template v-if="departureLocationByFlightNumber.code"> ({{
                                            departureLocationByFlightNumber.code }})</template></h3>
                                        <h3 v-else-if="departureLocationByFlightNumber.code">{{departureLocationByFlightNumber.code}}</h3>
                                    </div>
                                </template>
                                <template v-else-if="arrivalLocation && departureLocation">
                                    <div class="fs-booking-edit-autocomplete-text-line"></div>
                                    <div class="fs-booking-edit-autocomplete-text-item">
                                        <template v-if="departureLocation">
                                            <h3>{{ departureLocation.city }} ({{ departureLocation.code }})</h3>
                                        </template>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="fs-booking-edit-autocomplete-text-item">
                                        <h3 v-if="departureLocation">{{ departureLocation.city }} ({{
                                            departureLocation.code }})</h3>
                                    </div>
                                </template>
                                <template v-if="searchType == 'by_flight_number'">
                                    <v-icon icon="mdi-airplane" class="fs-airplane-icon"
                                        v-if="flights.length > 0 && departureLocationByFlightNumber && departureLocationByFlightNumber.city && arrivalLocationByFlightNumber && arrivalLocationByFlightNumber.city"></v-icon>
                                    <div class="fs-booking-edit-autocomplete-text-item"
                                        v-if="arrivalLocationByFlightNumber">
                                        <h3 v-if="arrivalLocationByFlightNumber.city">{{ arrivalLocationByFlightNumber.city }}<template v-if="arrivalLocationByFlightNumber.code"> ({{
                                            arrivalLocationByFlightNumber.code }})</template></h3>
                                        <h3 v-else-if="arrivalLocationByFlightNumber.code">{{arrivalLocationByFlightNumber.code}}</h3>
                                    </div>
                                </template>
                                <template v-else>
                                    <v-icon icon="mdi-airplane" class="fs-airplane-icon"
                                        v-if="arrivalLocation && departureLocation" />
                                    <div class="fs-booking-edit-autocomplete-text-item">
                                        <template v-if="arrivalLocation && departureLocation">
                                            <template v-if="arrivalLocation">
                                                <h3>{{ arrivalLocation.city }} ({{ arrivalLocation.code }})</h3>
                                            </template>
                                        </template>
                                        <template v-else>
                                            <h3 v-if="arrivalLocation">{{ arrivalLocation.city }} ({{
                                                arrivalLocation.code }})</h3>
                                        </template>
                                    </div>
                                </template>
                            </div>
                            <div class="fs-btn">
                                <v-btn type="button" variant="outlined" block color="primary" :loading="loading"
                                    :disabled="loading" @click="isSearchEnabled = true">
                                    {{ loading ? t('common.loading') : t('flight_status.edit_search') }}
                                </v-btn>
                            </div>
                        </v-form>
                    </v-card-text>
                </v-card>
            </div>
            END COMMENTED OUT -->
            <FlightStatusError
                v-if="error"
                :error="error"
                :error-code="errorCode"
                :error-details="errorDetails"
                :departure-location="departureLocation"
                :arrival-location="arrivalLocation"
                :selected-date="selectedDate"
            />
            <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 200px">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            </div>
            <FlightListing
                v-else-if="flights.length > 0 || returnFlights.length > 0"
                :flights="flights"
                :default-sortby="defaultSortby"
                :return-flights="returnFlights"
                :is-round-trip="isRoundTrip"
                :departure-date="selectedDate"
                :arrival-date="returnDate"
                :search-type="searchedType"
            />
        </v-container>
    </MainLayout>
</template>
